import { AppEvents } from '$scripts/events/events';
import { Outcome } from '$scripts/outcome';
import {
  AuthCodeOutcome,
  AuthFailedOutcome,
  RedirectOutcome,
  TokenOutcome,
} from './outcomes';

type OutcomeHandler = (
  events: AppEvents,
  outcome: Outcome
) => void | Promise<void>;

function createOutcomeHandler<F extends OutcomeHandler>(handler: F): F {
  return ((events: AppEvents, outcome: Outcome) => {
    if (!outcome.isHandled) {
      return handler(events, outcome);
    }
  }) as F;
}

// sendToken / sendAuthCode run the async emit-time origin gate (VULN-36931):
// they validate asserted_origin and only then release the credential (or emit
// on-auth-failed). We AWAIT them before outcome.setHandled() so `isHandled`
// never claims the outcome is done while the release/withhold is still pending
// — otherwise any code that later runs after handling (e.g. a modal teardown)
// would race the credential delivery.
export const defaultHandleToken = createOutcomeHandler(
  async (events: AppEvents, outcome: Outcome) => {
    if (outcome.of(TokenOutcome)) {
      const { token, fallbackUrl } = (outcome as TokenOutcome).getValue();
      await events.sendToken(token, fallbackUrl);
      outcome.setHandled();
    }
  }
);

export const defaultHandleAuthCode = createOutcomeHandler(
  async (events: AppEvents, outcome: Outcome) => {
    if (outcome.of(AuthCodeOutcome)) {
      const { code, fallbackUrl } = (outcome as AuthCodeOutcome).getValue();
      await events.sendAuthCode(code, fallbackUrl);
      outcome.setHandled();
    }
  }
);

export const defaultHandleAuthFailed = createOutcomeHandler(
  (events: AppEvents, outcome: Outcome) => {
    if (outcome.of(AuthFailedOutcome)) {
      const { reason, fallbackUrl } = (
        outcome as AuthFailedOutcome
      ).getValue();
      events.sendAuthFailed({ reason, fallbackUrl });
      outcome.setHandled();
    }
  }
);

export const defaultHandleRedirect = createOutcomeHandler(
  (events: AppEvents, outcome: Outcome) => {
    if (outcome.of(RedirectOutcome)) {
      const { url } = (outcome as RedirectOutcome).getValue();
      events.redirect(url);
      outcome.setHandled();
    }
  }
);
